import {
  type CorrectSchedulingBookingContactRequest,
  type CreateDemoScheduleBlockRequest,
  type CreateDemoScheduledIntakeInvitationRequest,
  type CreateSchedulingAppointmentRequest,
  type DemoAppointment,
  type DemoAppointmentMutationReceipt,
  DemoAppointmentMutationReceiptSchema,
  DemoAppointmentSchema,
  type DemoAvailabilityQuery,
  type DemoAvailabilityResponse,
  DemoAvailabilityResponseSchema,
  type DemoProviderAvailabilityRule,
  type DemoReminderChannel,
  type DemoReminderKind,
  type DemoReminderReceipt,
  DemoReminderReceiptSchema,
  type DemoScheduleBlock,
  DemoScheduleBlockSchema,
  type DemoScheduledIntakeInvitationReceipt,
  DemoScheduledIntakeInvitationReceiptSchema,
  type DemoSchedulingConfiguration,
  DemoSchedulingConfigurationSchema,
  type DemoSchedulingState,
  DemoSchedulingStateSchema,
  type IssuePublicBookingTokensResponse,
  type IssueVisitIntakeLinkRequest,
  type OpenedBookingIntakeLink,
  type SaveProviderAvailabilityRequest,
  type SchedulingErrorDetail,
  type SchedulingSaveResult,
  type SchedulingViewer,
  type SchedulingWalkIn,
  type UpdateDemoAppointmentRequest,
  type UpdateDemoAppointmentStatusRequest,
  type VisitIntakeLink,
  type VisitIntakeLinkResponse,
} from "@emr/contracts";
import { demoApiOrigin, demoFetch } from "./demoFetch";
import { SchedulingRequestError, schedulingRequestError } from "./schedulingErrors";
import { fullSchedulingAccess } from "./schedulingPermissions";

async function requestJson<T>(
  path: string,
  parse: (value: unknown) => T,
  options: {
    readonly method?: "GET" | "POST";
    readonly body?: unknown;
    readonly signal?: AbortSignal | undefined;
  } = {},
): Promise<T> {
  const response = await demoFetch(`${demoApiOrigin()}${path}`, {
    method: options.method ?? "GET",
    ...(options.body === undefined
      ? {}
      : {
          headers: { "content-type": "application/json" },
          body: JSON.stringify(options.body),
        }),
    cache: "no-store",
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });
  if (!response.ok) {
    throw await schedulingRequestError(
      response,
      `The synthetic scheduler request failed (${response.status})`,
    );
  }
  return parse(await response.json());
}

export function createDemoSchedulingIdempotencyKey(prefix: string): string {
  const random =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}-${random}`;
}

export type SchedulingAppointmentUpdate = UpdateDemoAppointmentRequest & {
  readonly expectedVersion?: number;
};
export type SchedulingStatusUpdate = UpdateDemoAppointmentStatusRequest & {
  readonly expectedVersion?: number;
};

/**
 * A schedule read: the shared presentation shape plus what the signed-in viewer may change. The
 * durable read also lists walk-ins with a desk-issued questionnaire link and says whether the
 * desk can issue one (PA-5); the in-memory prototype has neither, so both are optional.
 */
export type SchedulingClientState = DemoSchedulingState & {
  readonly viewer: SchedulingViewer;
  readonly walkIns?: readonly SchedulingWalkIn[] | undefined;
  readonly patientLinksEnabled?: boolean | undefined;
};

/**
 * A checked-in booking's live desk-issued questionnaire link (PA-5). The durable read adds it to
 * the appointment (SchedulingAppointmentSchema); the shared presentation type does not name it.
 */
export function appointmentIntakeLink(appointment: DemoAppointment): VisitIntakeLink | undefined {
  return (appointment as DemoAppointment & { readonly intakeLink?: VisitIntakeLink }).intakeLink;
}

/**
 * The questionnaire link "Ask for patient details" gave a returning patient's booking, once the
 * patient opened it (PA-10). The durable read adds it to the appointment
 * (SchedulingAppointmentSchema); the shared presentation type does not name it.
 */
export function appointmentOpenedDetailsLink(
  appointment: DemoAppointment,
): OpenedBookingIntakeLink | undefined {
  return (appointment as DemoAppointment & { readonly openedDetailsLink?: OpenedBookingIntakeLink })
    .openedDetailsLink;
}

export interface SchedulingSaveOptions {
  /** From an acknowledgement_required result: confirms exactly that impact on re-send. */
  readonly acknowledgementToken?: string | undefined;
}

/** Options for re-issuing a booking's patient links. */
export interface IssuePublicTokensOptions {
  /**
   * "Ask for patient details" (PA-10): the questionnaire link asks every section, so the intake
   * review can link or create the chart of a returning patient whose booking matched none.
   */
  readonly patientDetails?: true | undefined;
  /**
   * With patientDetails: the link the patient opened that the desk confirmed replacing
   * (openedDetailsLink.id). Without it, a link the patient opened is refused
   * (intake_link_opened), never cut off silently.
   */
  readonly replaceOpenedLinkId?: string | undefined;
}

export interface DemoSchedulingClient {
  /**
   * Re-issues a booking's patient links; the earlier ones stop working. Durable clinics only.
   * The questionnaire link is in the response while the questionnaire is pending or when
   * `patientDetails` asked for it.
   */
  issuePublicTokens?(
    appointmentId: string,
    expectedVersion: number,
    options?: IssuePublicTokensOptions,
  ): Promise<IssuePublicBookingTokensResponse>;
  revokePublicTokens?(appointmentId: string, expectedVersion: number): Promise<void>;
  /**
   * Issues (or re-issues) the questionnaire link for a visit already in the clinic: a walk-in,
   * or a checked-in booking whose questionnaire is not complete (PA-5). The response is the only
   * place the link's token appears. Durable clinics only; the prototype leaves it out.
   */
  issueVisitIntakeLink?(
    encounterId: string,
    request: IssueVisitIntakeLinkRequest,
    signal?: AbortSignal,
  ): Promise<VisitIntakeLinkResponse>;
  /**
   * Corrects a booking's phone or email, where its texts and emails go (owner decision O4,
   * PA-7). Name only the fields that change; an empty email removes it. Audited by field name.
   * Durable clinics only; the prototype leaves it out.
   */
  correctBookingContact?(
    appointmentId: string,
    request: CorrectSchedulingBookingContactRequest,
    signal?: AbortSignal,
  ): Promise<DemoAppointment>;
  getState(signal?: AbortSignal): Promise<SchedulingClientState>;
  getAvailability(
    query: DemoAvailabilityQuery,
    signal?: AbortSignal,
  ): Promise<DemoAvailabilityResponse>;
  /**
   * A staff booking. messagingDecision (PA-8: what the patient said about texts and emails) is
   * recorded by the durable schedule only; the prototype never sends it.
   */
  createAppointment(
    input: CreateSchedulingAppointmentRequest,
    signal?: AbortSignal,
  ): Promise<DemoAppointmentMutationReceipt>;
  updateAppointment(
    appointmentId: string,
    input: SchedulingAppointmentUpdate,
    signal?: AbortSignal,
  ): Promise<DemoAppointment>;
  updateAppointmentStatus(
    appointmentId: string,
    input: SchedulingStatusUpdate,
    signal?: AbortSignal,
  ): Promise<DemoAppointment>;
  sendReminder(
    appointmentId: string,
    input: {
      readonly kind: DemoReminderKind;
      readonly channel: DemoReminderChannel;
      readonly idempotencyKey: string;
    },
    signal?: AbortSignal,
  ): Promise<DemoReminderReceipt>;
  createScheduledIntakeInvitation(
    appointmentId: string,
    input: CreateDemoScheduledIntakeInvitationRequest,
    signal?: AbortSignal,
  ): Promise<DemoScheduledIntakeInvitationReceipt>;
  /**
   * Saves the clinic settings against configuration.version. Physician calendars are not part
   * of it: the server keeps its own providerAvailability and availabilityOverrides.
   */
  updateConfiguration(
    configuration: DemoSchedulingConfiguration,
    options?: SchedulingSaveOptions,
    signal?: AbortSignal,
  ): Promise<SchedulingSaveResult>;
  /** Replaces one physician's weekly rules and date overrides, against its availabilityVersion. */
  saveProviderAvailability(
    providerId: string,
    request: SaveProviderAvailabilityRequest,
    signal?: AbortSignal,
  ): Promise<SchedulingSaveResult>;
  createBlock(
    input: CreateDemoScheduleBlockRequest,
    signal?: AbortSignal,
  ): Promise<DemoScheduleBlock>;
  removeBlock(
    block: Pick<DemoScheduleBlock, "id" | "createdAt">,
    signal?: AbortSignal,
  ): Promise<void>;
}

function availabilityPath(query: DemoAvailabilityQuery): string {
  const parameters = new URLSearchParams({
    fromDate: query.fromDate,
    toDate: query.toDate,
    appointmentTypeId: query.appointmentTypeId,
    audience: "staff",
  });
  if (query.providerId !== undefined) parameters.set("providerId", query.providerId);
  return `/v1/demo/scheduling/availability?${parameters.toString()}`;
}

/** Whether every visit type a calendar window lists (or overrides) still exists. */
function windowTypesExist(
  window: Pick<DemoProviderAvailabilityRule, "appointmentTypeIds" | "durationOverrides">,
  appointmentTypeIds: ReadonlySet<string>,
): boolean {
  return (
    window.appointmentTypeIds.every((id) => appointmentTypeIds.has(id)) &&
    window.durationOverrides.every((override) => appointmentTypeIds.has(override.appointmentTypeId))
  );
}

/** The durable API's copy for a save that lost a race, so both modes read the same. */
const STALE_SAVE_MESSAGES = {
  scheduling_settings_changed:
    "Someone else saved the scheduling settings. Reload to see their changes.",
  provider_availability_changed:
    "Someone else saved this physician's calendar. Reload to see their changes.",
} as const satisfies Partial<Record<SchedulingErrorDetail, string>>;
type StaleSaveDetail = keyof typeof STALE_SAVE_MESSAGES;

function staleSave(detail: StaleSaveDetail): SchedulingRequestError {
  return new SchedulingRequestError(STALE_SAVE_MESSAGES[detail], {
    status: 409,
    code: "CONFLICT",
    detail,
  });
}

/**
 * The in-memory prototype stores one configuration document behind one version, and has no
 * roles. This adapter gives it the durable store's shape: a full-access viewer, settings saves
 * that leave physician calendars alone, and calendar saves with their own version. It never asks
 * for an acknowledgement, and date overrides need the durable store.
 */
export function createDemoSchedulingClient(): DemoSchedulingClient {
  /** Clinic versions this client's own calendar saves produced. */
  const calendarSaveVersions = new Set<number>();

  /*
   * A settings draft read at version N still applies when every later version came from this
   * client's calendar saves, because those changed nothing a settings save writes.
   */
  const settingsBaseVersion = (draftVersion: number, currentVersion: number): number => {
    let version = draftVersion;
    while (version < currentVersion && calendarSaveVersions.has(version + 1)) version += 1;
    return version === currentVersion ? currentVersion : draftVersion;
  };

  async function readState(signal?: AbortSignal): Promise<SchedulingClientState> {
    const state = await requestJson(
      "/v1/demo/scheduling/state",
      (value) => DemoSchedulingStateSchema.parse(value),
      { signal },
    );
    return {
      ...state,
      viewer: fullSchedulingAccess(state.configuration.providers.map((provider) => provider.id)),
    };
  }

  async function writeConfiguration(
    configuration: DemoSchedulingConfiguration,
    staleDetail: StaleSaveDetail,
    signal: AbortSignal | undefined,
  ): Promise<DemoSchedulingConfiguration> {
    try {
      return await requestJson(
        "/v1/demo/scheduling/configuration",
        (value) => DemoSchedulingConfigurationSchema.parse(value),
        {
          method: "POST",
          body: { expectedVersion: configuration.version, configuration },
          signal,
        },
      );
    } catch (caught) {
      if (caught instanceof SchedulingRequestError && caught.status === 409) {
        throw staleSave(staleDetail);
      }
      throw caught;
    }
  }

  return {
    getState: readState,

    getAvailability(query, signal) {
      return requestJson(
        availabilityPath(query),
        (value) => DemoAvailabilityResponseSchema.parse(value),
        { signal },
      );
    },

    createAppointment(input, signal) {
      // The prototype's schema refuses messagingDecision: it has no consent records.
      const { messagingDecision: _notInPrototype, ...body } = input;
      return requestJson(
        "/v1/demo/scheduling/appointments",
        (value) => DemoAppointmentMutationReceiptSchema.parse(value),
        { method: "POST", body, signal },
      );
    },

    updateAppointment(appointmentId, input, signal) {
      return requestJson(
        `/v1/demo/scheduling/appointments/${encodeURIComponent(appointmentId)}/update`,
        (value) => DemoAppointmentSchema.parse(value),
        { method: "POST", body: input, signal },
      );
    },

    updateAppointmentStatus(appointmentId, input, signal) {
      return requestJson(
        `/v1/demo/scheduling/appointments/${encodeURIComponent(appointmentId)}/status`,
        (value) => DemoAppointmentSchema.parse(value),
        { method: "POST", body: input, signal },
      );
    },

    sendReminder(appointmentId, input, signal) {
      return requestJson(
        `/v1/demo/scheduling/appointments/${encodeURIComponent(appointmentId)}/reminders`,
        (value) => DemoReminderReceiptSchema.parse(value),
        { method: "POST", body: input, signal },
      );
    },

    createScheduledIntakeInvitation(appointmentId, input, signal) {
      return requestJson(
        `/v1/demo/scheduling/appointments/${encodeURIComponent(appointmentId)}/intake-invitations`,
        (value) => DemoScheduledIntakeInvitationReceiptSchema.parse(value),
        { method: "POST", body: input, signal },
      );
    },

    async updateConfiguration(configuration, _options, signal) {
      const current = (await readState(signal)).configuration;
      const providerIds = new Set(configuration.providers.map((provider) => provider.id));
      const appointmentTypeIds = new Set(configuration.appointmentTypes.map((type) => type.id));
      const providerAvailability = current.providerAvailability.filter((rule) =>
        providerIds.has(rule.providerId),
      );
      const availabilityOverrides = current.availabilityOverrides.filter((override) =>
        providerIds.has(override.providerId),
      );
      if (
        [...providerAvailability, ...availabilityOverrides].some(
          (window) => !windowTypesExist(window, appointmentTypeIds),
        )
      ) {
        throw new SchedulingRequestError("Remove this visit type from physician calendars first.", {
          status: 409,
          code: "CONFLICT",
          detail: "visit_type_in_calendars",
        });
      }
      const availabilityVersions = new Map(
        current.providers.map((provider) => [provider.id, provider.availabilityVersion]),
      );
      const saved = await writeConfiguration(
        {
          ...configuration,
          version: settingsBaseVersion(configuration.version, current.version),
          providers: configuration.providers.map(
            ({ availabilityVersion: _ignored, ...provider }) => {
              const availabilityVersion = availabilityVersions.get(provider.id);
              return availabilityVersion === undefined
                ? provider
                : { ...provider, availabilityVersion };
            },
          ),
          providerAvailability,
          availabilityOverrides,
        },
        "scheduling_settings_changed",
        signal,
      );
      return { status: "saved", configuration: saved };
    },

    async saveProviderAvailability(providerId, request, signal) {
      if (request.availability.overrides.length > 0) {
        throw new SchedulingRequestError("Date overrides need the clinic schedule service.", {
          status: 422,
          code: "VALIDATION_FAILED",
        });
      }
      const current = (await readState(signal)).configuration;
      const provider = current.providers.find((candidate) => candidate.id === providerId);
      if (provider === undefined) {
        throw new SchedulingRequestError(
          "This physician isn't on the saved schedule. Save the scheduling settings first.",
          { status: 404, code: "NOT_FOUND" },
        );
      }
      const availabilityVersion = provider.availabilityVersion ?? 1;
      if (request.expectedVersion !== availabilityVersion) {
        throw staleSave("provider_availability_changed");
      }
      const saved = await writeConfiguration(
        {
          ...current,
          providers: current.providers.map((candidate) =>
            candidate.id === providerId
              ? { ...candidate, availabilityVersion: availabilityVersion + 1 }
              : candidate,
          ),
          providerAvailability: [
            ...current.providerAvailability.filter((rule) => rule.providerId !== providerId),
            ...request.availability.rules.map((rule) => ({
              ...rule,
              id: createDemoSchedulingIdempotencyKey("availability"),
              providerId,
            })),
          ],
          availabilityOverrides: current.availabilityOverrides.filter(
            (override) => override.providerId !== providerId,
          ),
        },
        "provider_availability_changed",
        signal,
      );
      calendarSaveVersions.add(saved.version);
      return { status: "saved", configuration: saved };
    },

    createBlock(input, signal) {
      return requestJson(
        "/v1/demo/scheduling/blocks",
        (value) => DemoScheduleBlockSchema.parse(value),
        { method: "POST", body: input, signal },
      );
    },

    async removeBlock(block, signal) {
      await requestJson(
        `/v1/demo/scheduling/blocks/${encodeURIComponent(block.id)}/remove`,
        (value) => {
          const result = value as { readonly removed?: unknown; readonly blockId?: unknown };
          if (result.removed !== true || result.blockId !== block.id) {
            throw new Error("The synthetic scheduler returned an invalid block removal receipt");
          }
        },
        {
          method: "POST",
          body: { expectedCreatedAt: block.createdAt },
          signal,
        },
      );
    },
  };
}

export const demoSchedulingClient: DemoSchedulingClient = createDemoSchedulingClient();

/** The patient app's origin, for booking links and the website widget's script address. */
export function patientAppOrigin(): string {
  const configured = import.meta.env.VITE_PATIENT_APP_ORIGIN;
  return typeof configured === "string" && configured.length > 0
    ? configured
    : "http://localhost:5174";
}

/**
 * Whether patient links point somewhere a phone can reach. A deployed build without
 * VITE_PATIENT_APP_ORIGIN would send patients to localhost, so it issues no phone codes.
 */
export function patientAppOriginConfigured(): boolean {
  const configured = import.meta.env.VITE_PATIENT_APP_ORIGIN;
  return (typeof configured === "string" && configured.length > 0) || !import.meta.env.PROD;
}

export function demoPublicBookingUrl(path: string): string {
  return new URL(path, patientAppOrigin()).toString();
}
